/**
 * Embedded (in-app) webview detection.
 *
 * Google's OAuth screen refuses to render inside in-app webviews (its
 * "use secure browsers" policy), so a user who opens a HelloAnt link from
 * inside LINE / Facebook / Instagram and tries to sign in with Google hits
 * a dead-end "access blocked" page. We can't programmatically eject a page
 * from a webview (no cross-platform API), so the best we can do is detect
 * the webview before redirecting to Logto and tell the user to reopen the
 * link in their real browser.
 *
 * Detection is user-agent sniffing, which is inherently fragile — app
 * versions change their UA tokens over time. We therefore keep matching
 * conservative (well-known, stable tokens only) and the calling UI always
 * offers a "continue anyway" escape hatch, so a false positive costs the
 * user one extra tap rather than locking them out.
 */

export type EmbeddedBrowserApp = 'line' | 'facebook' | 'instagram' | 'messenger' | 'other';

export interface EmbeddedBrowserInfo {
  isEmbedded: boolean;
  app: EmbeddedBrowserApp | null;
}

export function detectEmbeddedBrowser(ua: string): EmbeddedBrowserInfo {
  // LINE injects e.g. "Line/13.4.0" (the trailing slash avoids matching
  // unrelated words containing "line").
  if (/\bLine\//i.test(ua)) return { isEmbedded: true, app: 'line' };

  // Messenger checked before Facebook: the Messenger UA also carries FB
  // tokens, and we want the more specific label to win.
  if (/\bMessenger\//i.test(ua)) return { isEmbedded: true, app: 'messenger' };

  // Facebook app webview: FBAN (app name) / FBAV (app version).
  if (/\bFBA[NV]\//i.test(ua)) return { isEmbedded: true, app: 'facebook' };

  if (/\bInstagram\b/i.test(ua)) return { isEmbedded: true, app: 'instagram' };

  return { isEmbedded: false, app: null };
}

/** Convenience wrapper reading the live UA. SSR-safe (returns "not embedded"
 *  when there is no navigator). */
export function getEmbeddedBrowserInfo(): EmbeddedBrowserInfo {
  if (typeof navigator === 'undefined') return { isEmbedded: false, app: null };
  return detectEmbeddedBrowser(navigator.userAgent);
}
